import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import ar from './locale/ar.json';
import en from './locale/en.json';

export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const RTL_LANGUAGES: readonly SupportedLanguage[] = ['ar'];

export const LANGUAGE_STORAGE_KEY = 'fuel-benchmark.language';

export const isSupportedLanguage = (value: unknown): value is SupportedLanguage =>
  typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

export const directionOf = (language: string): 'ltr' | 'rtl' => (RTL_LANGUAGES.includes(language as SupportedLanguage) ? 'rtl' : 'ltr');

/** Keeps `<html lang>` and `<html dir>` in step with the active language so RTL styling works. */
export const applyDocumentLanguage = (language: string): void => {
  document.documentElement.lang = language;
  document.documentElement.dir = directionOf(language);
};

const storedLanguage = (): SupportedLanguage => {
  try {
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

    return isSupportedLanguage(value) ? value : 'en';
  } catch {
    return 'en';
  }
};

void i18next.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ar: { translation: ar },
  },
  lng: storedLanguage(),
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LANGUAGES],
  interpolation: { escapeValue: false },
  returnNull: false,
});

applyDocumentLanguage(i18next.language);

i18next.on('languageChanged', (language) => {
  applyDocumentLanguage(language);

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Storage can be unavailable (private mode); the language still applies for this session.
  }
});

export { i18next };
